import { useMutation } from '@apollo/client'
import { Chip, Stack } from '@mui/material'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'
import { GetCompanyClientsQuery } from '__generated__/graphql'
import React, { useMemo } from 'react'
import {
    MakeClientCompanyAdmin,
    RemoveClientRole,
} from 'recruiter/graphql/clients.gql'
import { CreateImpersonationCode } from 'recruiter/graphql/impersonation.gql'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError, getPxAsRem } from 'shared/utils'
import PermissionsActions from './PermissionsActions'

export interface ClientPermissionsTableProps {
    data: GetCompanyClientsQuery | undefined
    searchInput: string
    refetch: () => void
}

export interface ClientPermissionsTableRow {
    id: number
    clientId: string
    userId: string
    name: string
    email: string
    lastActive: string | null
    isAdmin: boolean
}

const ClientPermissionsTable: React.FC<ClientPermissionsTableProps> = ({
    data,
    searchInput,
    refetch,
}) => {
    // Memoize filtered and sorted clients
    const toast = useToast()
    const clients = useMemo(() => {
        return (
            data?.company?.clients
                ?.filter(
                    (client) =>
                        client &&
                        client.user?.preferredName
                            ?.toLowerCase()
                            .includes(searchInput.toLowerCase())
                )
                .sort((a, b) => {
                    const aIsAdmin = !!a.isAdmin
                    const bIsAdmin = !!b.isAdmin
                    return aIsAdmin === bIsAdmin ? 0 : aIsAdmin ? -1 : 1
                }) ?? []
        )
    }, [data, searchInput])

    const [makeAdminMutation] = useMutation(MakeClientCompanyAdmin)
    const [removeClientMutation] = useMutation(RemoveClientRole)
    const [impersonateMutation] = useMutation(CreateImpersonationCode)

    const makeAdmin = React.useCallback(
        async (clientId: string) => {
            makeAdminMutation({
                variables: { clientId, companyId: data?.company?.id ?? '' },
                onCompleted: (data) => {
                    if (data.makeAdmin.success) {
                        toast({
                            title: 'Success',
                            severity: 'success',
                            message: 'Client admin status updated successfully',
                        })
                        refetch()
                    } else {
                        toast({
                            title: 'Error',
                            severity: 'error',
                            message:
                                data.makeAdmin.message ??
                                'Failed to update client admin status. Please try again.',
                        })
                    }
                },
                onError: (error) => {
                    toast({
                        title: 'Error',
                        severity: 'error',
                        message: formatGraphQLError(
                            error,
                            'Failed to update client admin status. Please try again.'
                        ),
                    })
                },
            })
        },
        [makeAdminMutation, data?.company?.id, toast, refetch]
    )

    const removeClient = React.useCallback(
        async (clientId: string) => {
            removeClientMutation({
                variables: { clientId, companyId: data?.company?.id ?? '' },
                onCompleted: (data) => {
                    if (data.removeClientRole.success) {
                        toast({
                            title: 'Success',
                            severity: 'success',
                            message: 'Client removes successfully',
                        })
                        refetch()
                    } else {
                        toast({
                            title: 'Error',
                            severity: 'error',
                            message:
                                data.removeClientRole.message ??
                                'Failed to remove client. Please try again.',
                        })
                    }
                },
                onError: (error) => {
                    toast({
                        title: 'Error',
                        severity: 'error',
                        message: formatGraphQLError(
                            error,
                            'Failed to remove client. Please try again.'
                        ),
                    })
                },
            })
        },
        [removeClientMutation, data?.company?.id, toast, refetch]
    )

    const impersonate = React.useCallback(
        async (userId: string) => {
            impersonateMutation({
                variables: { targetUserId: userId, targetRole: 'client' },
                onCompleted: (data) => {
                    window.open(
                        data.createImpersonationCode.redirectUrl,
                        '_blank'
                    )
                },
                onError: (error) => {
                    toast({
                        title: 'Error',
                        severity: 'error',
                        message: formatGraphQLError(
                            error,
                            'Failed to impersonate client. Please try again.'
                        ),
                    })
                },
            })
        },
        [impersonateMutation, toast]
    )

    // Define columns
    const columns: GridColDef<ClientPermissionsTableRow>[] = useMemo(
        () => [
            {
                field: 'name',
                headerName: 'User',
                flex: 1,
                sortable: false,
                minWidth: parseInt(getPxAsRem(200), 10),
                renderCell: (params) => (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <span>{params.row.name}</span>
                        {params.row.isAdmin && (
                            <Chip size="small" label="Admin" />
                        )}
                    </Stack>
                ),
            },
            {
                field: 'email',
                headerName: 'Email',
                flex: 1,
                sortable: false,
                minWidth: parseInt(getPxAsRem(150), 10),
                renderCell: (params) => <span>{params.row.email}</span>,
            },
            {
                field: 'lastActive',
                headerName: 'Last Active',
                sortable: false,
                minWidth: parseInt(getPxAsRem(150), 10),
                renderCell: (params) => (
                    <span>{params.row.lastActive ?? '-'}</span>
                ),
            },
            {
                field: 'actions',
                headerName: 'Actions',
                minWidth: parseInt(getPxAsRem(100), 10),
                sortable: false,
                renderCell: (params) => (
                    <PermissionsActions
                        isAdmin={params.row.isAdmin}
                        onMakeAdmin={() =>
                            makeAdmin(params.row.clientId.toString())
                        }
                        onRemoveUser={() =>
                            removeClient(params.row.clientId.toString())
                        }
                        onImpersonate={() => impersonate(params.row.userId)}
                    />
                ),
            },
        ],
        [makeAdmin, removeClient, impersonate]
    )

    // Map rows
    const rows: ClientPermissionsTableRow[] = useMemo(
        () =>
            clients.map((client, index) => ({
                id: index,
                clientId: client.id,
                userId: client.user?.id ?? '',
                name: client.user?.preferredName ?? '-',
                email: client.user?.email ?? '-',
                lastActive: client.lastLogin,
                isAdmin: client.isAdmin,
            })),
        [clients]
    )

    return (
        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
            <DataGridPro
                sx={{ border: 0, width: '100%' }}
                disableColumnMenu
                hideFooter
                disableColumnReorder
                disableColumnResize
                showCellVerticalBorder={false}
                showColumnVerticalBorder={false}
                columns={columns}
                rows={rows}
            />
        </div>
    )
}

export default ClientPermissionsTable
